import type { ReactNode } from 'react';
import { Document, Image, PDFDownloadLink, Page, StyleSheet, Text, View } from '@react-pdf/renderer';
import { FileDown } from 'lucide-react';
import { calculateSampleNom35, type Nom35RiskLevel } from '@/lib/nom35';
import type { Nom35ExecutiveGlobalResult } from '@/lib/nom35Executive';
import {
  analyzeG1DemographicExecutive,
  analyzeRiskDemographicExecutive,
  buildNom35ExecutiveSummaryBlocks,
  buildOfficialCategoryRows,
  domainNarrativeParagraph,
  expandedCategoryNarrative,
  nom35KeyFindingsAndPhasedPlan,
  type DomainRow,
} from '@/lib/nom35ExecutivePdfInterpretation';
import { cn } from '@/lib/utils';

export interface Nom35ExecutiveReportPdfProps {
  data: Nom35ExecutiveGlobalResult;
  companyName: string;
  centerName: string;
  date: string;
  clientLogoUrl?: string;
}

const COLORS = {
  title: '#1e3a8a',
  subtitle: '#2563eb',
  text: '#374151',
  border: '#e5e7eb',
  headerBg: '#f9fafb',
  muted: '#6b7280',
  interpretBg: '#eff6ff', // Fondo azul muy claro para interpretaciones
  interpretBorder: '#bfdbfe'
};

const styles = StyleSheet.create({
  page: {
    paddingLeft: 40,
    paddingRight: 40,
    paddingTop: 56,
    paddingBottom: 56,
    fontFamily: 'Helvetica',
  },
  h1: { fontSize: 18, color: COLORS.title, marginBottom: 14, fontWeight: 'bold' },
  h2: { fontSize: 13, color: COLORS.subtitle, marginBottom: 8, marginTop: 12, fontWeight: 'bold' },
  h3: { fontSize: 11, color: COLORS.subtitle, marginBottom: 6, marginTop: 10, fontWeight: 'bold' },
  p: { fontSize: 10.5, color: COLORS.text, lineHeight: 1.5, textAlign: 'justify', marginBottom: 10 },

  interpretationBox: {
    backgroundColor: '#f8fafc',
    borderRadius: 4,
    borderLeftWidth: 3,
    borderLeftColor: COLORS.subtitle,
    padding: 10,
    marginBottom: 16,
    marginTop: -8,
  },
  interpretationText: { fontSize: 10, color: '#1e40af', lineHeight: 1.5, fontStyle: 'italic' },

  footer: {
    position: 'absolute', left: 40, right: 40, bottom: 22, borderTopWidth: 1,
    borderTopColor: COLORS.border, paddingTop: 6, flexDirection: 'row', justifyContent: 'space-between',
  },
  footerText: { fontSize: 9, color: COLORS.muted },

  coverWrap: { flexGrow: 1, justifyContent: 'center' },
  coverLogoWrap: { alignItems: 'center', marginBottom: 60 },
  logoWrap: { alignItems: 'center', marginBottom: 22 },
  logo: { width: 180, height: 64, objectFit: 'contain' },
  coverTitle: { fontSize: 20, color: COLORS.title, fontWeight: 'bold', textAlign: 'center', marginBottom: 12, lineHeight: 1.3 },
  coverSubtitle: { fontSize: 14, color: COLORS.subtitle, textAlign: 'center', marginBottom: 40 },
  accentLine: { width: 60, height: 4, backgroundColor: COLORS.subtitle, alignSelf: 'center', marginBottom: 40 },
  coverCompany: { fontSize: 22, color: COLORS.text, fontWeight: 'bold', textAlign: 'center', marginBottom: 8 },
  coverCenter: { fontSize: 14, color: COLORS.muted, textAlign: 'center', marginBottom: 24 },
  coverDate: { fontSize: 11, color: COLORS.muted, textAlign: 'center' },

  table: { width: '100%', borderWidth: 1, borderColor: COLORS.border, marginBottom: 16 },
  row: { flexDirection: 'row', borderBottomWidth: 1, borderBottomColor: '#EEE', paddingVertical: 4 },
  rowLast: { flexDirection: 'row', paddingVertical: 4 },
  colLabel: { width: '40%', paddingHorizontal: 8, justifyContent: 'center' },
  colValue: { width: '30%', paddingHorizontal: 8, justifyContent: 'center' },
  colRisk: { width: '30%', paddingHorizontal: 8, justifyContent: 'center' },
  colTwoLeft: { width: '58%', paddingHorizontal: 8, justifyContent: 'center' },
  colTwoRight: { width: '42%', paddingHorizontal: 8, justifyContent: 'center' },
  sixColLabel: { width: '35%', paddingHorizontal: 6, justifyContent: 'center' },
  sixColRisk: { width: '13%', paddingHorizontal: 2, justifyContent: 'center' },
  cellHeaderWrap: { backgroundColor: COLORS.headerBg, paddingVertical: 5 },
  cellHeader: { fontSize: 10, fontWeight: 'bold', color: '#1f2937' },
  cellLeft: { fontSize: 10, color: COLORS.text },
  cellCenter: { fontSize: 10, color: COLORS.text, textAlign: 'center' },
  cellRight: { fontSize: 10, color: COLORS.text, textAlign: 'right' },
  formulaBox: { borderWidth: 1, borderColor: COLORS.border, backgroundColor: COLORS.headerBg, padding: 10, marginBottom: 10 },
  formulaLine: { fontSize: 10, color: COLORS.text, marginTop: 4, lineHeight: 1.45 },

  headerFixed: {
    position: 'absolute',
    top: 20,
    left: 40,
    right: 40,
    borderBottomWidth: 1,
    borderBottomColor: COLORS.border,
    paddingBottom: 5,
    flexDirection: 'row',
    justifyContent: 'flex-end',
  },
  headerFixedText: { fontSize: 8, color: COLORS.muted, textTransform: 'uppercase' },

  closingText: { fontSize: 10.5, color: COLORS.text, lineHeight: 1.5, textAlign: 'justify', marginTop: 14, marginBottom: 8 },
});

function riskColor(level: Nom35RiskLevel): string {
  if (level === 'Muy Alto') return '#dc2626';
  if (level === 'Alto') return '#ea580c';
  if (level === 'Medio') return '#ca8a04';
  if (level === 'Bajo') return '#16a34a';
  return '#6b7280';
}

function frequencyRowsG2G3(table: NonNullable<Nom35ExecutiveGlobalResult['g2g3']>['crosses']['riskBySex']): Array<[string, string]> {
  return Object.entries(table)
    .filter(([, v]) => v.total > 0)
    .sort((a, b) => b[1].total - a[1].total)
    .map(([k, v]) => [k, String(v.total)]);
}

function frequencyRowsG1(table: Record<string, { requiereAtencion: number; noRequiere: number; total: number }>): Array<[string, string]> {
  return Object.entries(table)
    .filter(([, v]) => v.total > 0)
    .sort((a, b) => b[1].total - a[1].total)
    .map(([k, v]) => [k, String(v.total)]);
}

// ------------------------------------------------------

function HeaderFixed() {
  return (
    <View style={styles.headerFixed} fixed>
      <Text style={styles.headerFixedText}>Informe de Resultados NOM-035</Text>
    </View>
  );
}

function FooterFixed() {
  return (
    <View style={styles.footer} fixed>
      <Text style={styles.footerText}>Reporte NOM-035-STPS-2018 | PsyTalent</Text>
      <Text style={styles.footerText} render={({ pageNumber, totalPages }) => `Página ${pageNumber} de ${totalPages}`} />
    </View>
  );
}

function PageFrame({ children }: { children: ReactNode }) {
  return <Page size="LETTER" style={styles.page}>{children}</Page>;
}

function TwoColTable({ headers, rows }: { headers: [string, string]; rows: Array<[string, string]>; }) {
  return (
    <View style={styles.table}>
      <View style={styles.row} wrap={false}>
        <View style={[styles.colTwoLeft, styles.cellHeaderWrap]}><Text style={styles.cellHeader}>{headers[0]}</Text></View>
        <View style={[styles.colTwoRight, styles.cellHeaderWrap]}><Text style={[styles.cellHeader, { textAlign: 'right' }]}>{headers[1]}</Text></View>
      </View>
      {rows.map((r, i) => (
        <View key={i} style={i === rows.length - 1 ? styles.rowLast : styles.row} wrap={false}>
          <View style={styles.colTwoLeft}><Text style={styles.cellLeft}>{r[0]}</Text></View>
          <View style={styles.colTwoRight}><Text style={styles.cellRight}>{r[1]}</Text></View>
        </View>
      ))}
    </View>
  );
}

function ThreeColTable({ headers, rows, colorizeRisk = false }: { headers: [string, string, string]; rows: Array<[string, string, string]>; colorizeRisk?: boolean; }) {
  return (
    <View style={styles.table}>
      <View style={styles.row} wrap={false}>
        <View style={[styles.colLabel, styles.cellHeaderWrap]}><Text style={styles.cellHeader}>{headers[0]}</Text></View>
        <View style={[styles.colValue, styles.cellHeaderWrap]}><Text style={[styles.cellHeader, { textAlign: 'center' }]}>{headers[1]}</Text></View>
        <View style={[styles.colRisk, styles.cellHeaderWrap]}><Text style={[styles.cellHeader, { textAlign: 'right' }]}>{headers[2]}</Text></View>
      </View>
      {rows.map((r, i) => (
        <View key={i} style={i === rows.length - 1 ? styles.rowLast : styles.row} wrap={false}>
          <View style={styles.colLabel}><Text style={styles.cellLeft}>{r[0]}</Text></View>
          <View style={styles.colValue}><Text style={styles.cellCenter}>{r[1]}</Text></View>
          <View style={styles.colRisk}>
            <Text style={[styles.cellRight, colorizeRisk ? { color: riskColor(r[2] as Nom35RiskLevel), fontWeight: 'bold' } : {}]}>{r[2]}</Text>
          </View>
        </View>
      ))}
    </View>
  );
}

type SixHeaders = [string, string, string, string, string, string];
type SixRow = [string, string, string, string, string, string];

function SixColTable({ headers, rows }: { headers: SixHeaders; rows: SixRow[] }) {
  return (
    <View style={styles.table}>
      <View style={styles.row} wrap={false}>
        <View style={[styles.sixColLabel, styles.cellHeaderWrap]}>
          <Text style={[styles.cellHeader, { textAlign: 'left' }]}>{headers[0]}</Text>
        </View>
        {[1, 2, 3, 4, 5].map((j) => (
          <View key={`h-${j}`} style={[styles.sixColRisk, styles.cellHeaderWrap]}>
            <Text style={[styles.cellHeader, { textAlign: 'center' }]}>{headers[j]}</Text>
          </View>
        ))}
      </View>
      {rows.map((r, i) => (
        <View key={i} style={i === rows.length - 1 ? styles.rowLast : styles.row} wrap={false}>
          <View style={styles.sixColLabel}>
            <Text style={[styles.cellLeft, { textAlign: 'left' }]}>{r[0]}</Text>
          </View>
          {[1, 2, 3, 4, 5].map((j) => (
            <View key={`c-${j}`} style={styles.sixColRisk}>
              <Text style={styles.cellCenter}>{r[j]}</Text>
            </View>
          ))}
        </View>
      ))}
    </View>
  );
}

function CategoryTable({
  categoryName,
  domains,
  interpretationText,
  expandedNarrative,
}: {
  categoryName: string;
  domains: DomainRow[];
  interpretationText: string;
  expandedNarrative: string;
}) {
  const rows: Array<[string, string, string]> = domains.map((d) => [d.name, d.score.toFixed(2), d.risk]);
  const domainNotes = domains
    .map((d, idx) => {
      const note = domainNarrativeParagraph(d.name, d.risk);
      return note ? { key: `${d.name}-${idx}`, note } : null;
    })
    .filter((x): x is { key: string; note: string } => x !== null);
  return (
    <View>
      <Text style={styles.h2}>{categoryName}</Text>
      <ThreeColTable headers={['Concepto', 'Valor', 'Riesgo']} rows={rows} colorizeRisk />
      <View style={styles.interpretationBox}>
        <Text style={styles.interpretationText}>{interpretationText}</Text>
      </View>
      <Text style={styles.h2}>Interpretación ampliada por categoría</Text>
      <Text style={styles.p}>{expandedNarrative}</Text>
      {domainNotes.length > 0 ? (
        <>
          <Text style={styles.h3}>Lectura por dominio (riesgo medio, alto o muy alto)</Text>
          {domainNotes.map(({ key, note }) => (
            <View key={key} style={[styles.interpretationBox, { marginBottom: 8 }]}>
              <Text style={styles.interpretationText}>{note}</Text>
            </View>
          ))}
        </>
      ) : null}
    </View>
  );
}

function summarizeG1Interpretation(g1: NonNullable<Nom35ExecutiveGlobalResult['g1']>): string {
  if (g1.totalEvaluated <= 0) return 'No se cuenta con casos suficientes para interpretar la Guía I en esta campaña.';
  const pct = (g1.requiresAttentionCount / g1.totalEvaluated) * 100;
  if (pct >= 25) return `Se observa una proporción relevante de casos con potencial afectación clínica (${pct.toFixed(1)}%). Se recomienda priorizar entrevistas de primer contacto y canalización oportuna.`;
  if (pct >= 10) return `La prevalencia de casos con atención clínica potencial es moderada (${pct.toFixed(1)}%). Conviene reforzar detección temprana y seguimiento individual.`;
  return `La prevalencia de casos que requieren atención clínica es baja (${pct.toFixed(1)}%), manteniendo vigilancia preventiva y protocolos de respuesta.`;
}

function topRiskFromDomains(rows: DomainRow[]): Nom35RiskLevel {
  const order: Nom35RiskLevel[] = ['Nulo', 'Bajo', 'Medio', 'Alto', 'Muy Alto'];
  return rows.reduce<Nom35RiskLevel>((acc, item) => (order.indexOf(item.risk) > order.indexOf(acc) ? item.risk : acc), 'Nulo');
}

function categoryInterpretationText(rows: DomainRow[]): string {
  const top = topRiskFromDomains(rows);
  if (top === 'Muy Alto') return 'Interpretación: La categoría presenta un nivel crítico en al menos un dominio. Se recomienda intervención inmediata con responsables, plazos y seguimiento mensual.';
  if (top === 'Alto') return 'Interpretación: La categoría requiere acciones correctivas prioritarias y revisión de prácticas organizacionales asociadas al dominio con mayor puntaje.';
  if (top === 'Medio') return 'Interpretación: La categoría muestra exposición moderada. Se sugiere reforzar medidas preventivas y revisar cargas o dinámicas en áreas específicas.';
  if (top === 'Bajo') return 'Interpretación: La categoría se mantiene en nivel bajo. Se recomienda sostener acciones de prevención y monitoreo periódico.';
  return 'Interpretación: La categoría se ubica en nivel nulo, manteniendo condiciones favorables y vigilancia preventiva.';
}

function suggestedActionsTextForCategory(rows: DomainRow[]): string {
  const top = topRiskFromDomains(rows);
  if (top === 'Alto' || top === 'Muy Alto') {
    return 'Dada la lectura de riesgo en esta categoría, conviene alinear un programa de intervención con lo establecido en la NOM-035 y con el plan de trabajo del centro: grupos focales, revisión de políticas internas y compromisos medibles con responsables y plazos. La implementación y documentación corresponden a la organización.';
  }
  if (top === 'Medio') {
    return 'Se recomienda reforzar las medidas preventivas, revisar cargas o dinámicas específicas en las áreas afectadas y monitorear el avance.';
  }
  return 'Para mantener los niveles de riesgo controlados, se recomienda continuar con la difusión de la Política de Prevención de Riesgos Psicosociales y fomentar canales de comunicación abiertos.';
}

function CoverPage({ companyName, centerName, date, clientLogoUrl }: Nom35ExecutiveReportPdfProps) {
  return (
    <PageFrame>
      <View style={styles.coverWrap}>
        <View style={styles.coverLogoWrap}>{clientLogoUrl ? <Image src={clientLogoUrl} style={styles.logo} /> : null}</View>
        <Text style={styles.coverTitle}>INFORME EJECUTIVO DE RESULTADOS</Text>
        <Text style={styles.coverSubtitle}>Evaluación del Entorno Organizacional NOM-035-STPS-2018</Text>
        <View style={styles.accentLine} />
        <Text style={styles.coverCompany}>{companyName}</Text>
        <Text style={styles.coverCenter}>Centro de Trabajo: {centerName === 'NOM35' ? 'Sede Principal' : centerName}</Text>
        <Text style={styles.coverDate}>Fecha de emisión: {date}</Text>
      </View>
    </PageFrame>
  );
}

function IntroMethodologyPage({ totalPopulation, evaluated }: { totalPopulation: number; evaluated: number }) {
  const nMin = calculateSampleNom35(totalPopulation);
  const participationPct = totalPopulation > 0 ? (evaluated / totalPopulation) * 100 : 0;
  const interpretationRows: Array<[string, string, string]> = [
    ['Nulo', 'Riesgo mínimo', 'Mantener monitoreo'],
    ['Bajo', 'Riesgo controlable', 'Refuerzo preventivo'],
    ['Medio', 'Riesgo moderado', 'Intervención focalizada'],
    ['Alto', 'Riesgo significativo', 'Acción correctiva pronta'],
    ['Muy Alto', 'Riesgo crítico', 'Intervención inmediata'],
  ];
  return (
    <PageFrame>
      <Text style={styles.h1}>Introducción y Objetivos</Text>
      <Text style={styles.p}>
        El presente documento constituye el informe de resultados de la identificación y análisis de los factores de riesgo psicosocial y la evaluación del entorno organizacional en el centro de trabajo, en estricto cumplimiento con lo establecido en la Norma Oficial Mexicana NOM-035-STPS-2018.
      </Text>
      <Text style={styles.p}>
        El objetivo principal de esta evaluación es identificar áreas de oportunidad dentro de la dinámica laboral, analizar la exposición de los colaboradores a factores que pueden provocar trastornos de ansiedad, estrés grave derivado de la naturaleza de las funciones, el tipo de jornada laboral y la exposición a acontecimientos traumáticos severos.
      </Text>
      <Text style={styles.h2}>Metodología Aplicada y Muestra</Text>
      <Text style={styles.p}>
        La recolección de datos se llevó a cabo mediante la aplicación de los cuestionarios avalados por la STPS. Para garantizar la validez estadística, se calculó una muestra representativa:
      </Text>
      <View style={styles.formulaBox}>
        <Text style={[styles.cellHeader, { marginBottom: 6 }]}>Cálculo de muestra mínima (NOM-035-STPS-2018)</Text>
        <Text style={styles.cellLeft}>Fórmula: n = (0.9604 × N) / (0.0025 × (N - 1) + 0.9604)</Text>
        <Text style={styles.formulaLine}>
          <Text style={{ fontWeight: 'bold' }}>Población total (N): </Text>
          {totalPopulation}
        </Text>
        <Text style={styles.formulaLine}>
          <Text style={{ fontWeight: 'bold' }}>Muestra mínima requerida (n): </Text>
          {nMin}
        </Text>
        <Text style={styles.formulaLine}>
          <Text style={{ fontWeight: 'bold' }}>Muestra efectivamente evaluada: </Text>
          {evaluated}
          {' '}
          <Text style={{ fontWeight: 'bold' }}>(participación: </Text>
          {participationPct.toFixed(1)}%<Text style={{ fontWeight: 'bold' }}>)</Text>
        </Text>
      </View>
      <Text style={styles.h2}>Tabla de Interpretación</Text>
      <ThreeColTable headers={['Nivel', 'Interpretación', 'Acción']} rows={interpretationRows} />
    </PageFrame>
  );
}

function ExecutiveSummaryPage({ data }: { data: Nom35ExecutiveGlobalResult }) {
  const b = buildNom35ExecutiveSummaryBlocks(data);
  return (
    <PageFrame>
      <Text style={styles.h1}>Resumen ejecutivo</Text>
      <Text style={styles.p}>{b.globalRiskLine}</Text>
      <Text style={styles.p}>{b.criticalDomainsLine}</Text>
      <Text style={styles.p}>{b.demographicLine}</Text>
      <Text style={styles.h2}>Lectura para la organización</Text>
      <Text style={styles.p}>{b.executiveReading}</Text>
      <Text style={styles.h2}>Recomendación prioritaria</Text>
      <Text style={styles.p}>{b.priorityRecommendation}</Text>
    </PageFrame>
  );
}

function DemographicProfilePage({ data }: { data: Nom35ExecutiveGlobalResult }) {
  const g2 = data.g2g3;
  const g1 = data.g1;
  const sourceLabel = g2 ? 'Guía II / III (factores de riesgo psicosocial y entorno organizacional)' : 'Guía I (acontecimientos traumáticos severos)';
  const sexRows = g2
    ? frequencyRowsG2G3(g2.crosses.riskBySex)
    : g1
      ? frequencyRowsG1(g1.crosses.bySex)
      : [];
  const ageRows = g2
    ? frequencyRowsG2G3(g2.crosses.riskByAgeRange)
    : g1
      ? frequencyRowsG1(g1.crosses.byAgeRange)
      : [];
  const deptRows = g2
    ? frequencyRowsG2G3(g2.crosses.riskByDepartment)
    : g1
      ? frequencyRowsG1(g1.crosses.byDepartment)
      : [];
  const totalSample = g2?.validAssessmentCount ?? g1?.totalEvaluated ?? 0;

  return (
    <PageFrame>
      <Text style={styles.h1}>Perfil Sociodemográfico de la Muestra</Text>
      <Text style={styles.p}>
        La siguiente sección describe la composición de la muestra efectivamente evaluada en esta campaña, con base en las tablas de frecuencia demográfica del instrumento aplicado (
        {sourceLabel}
        ). Los conteos corresponden al número de participantes por categoría, sin inferir aún niveles de riesgo psicosocial en este apartado.
      </Text>
      <Text style={styles.p}>
        <Text style={{ fontWeight: 'bold' }}>Total de participantes en la muestra: </Text>
        {totalSample}
      </Text>
      <Text style={styles.h2}>Distribución por sexo</Text>
      <TwoColTable headers={['Sexo', 'Participantes (n)']} rows={sexRows.length ? sexRows : [['Sin datos', '0']]} />
      <Text style={styles.h2}>Distribución por rango de edad</Text>
      <TwoColTable headers={['Rango de edad', 'Participantes (n)']} rows={ageRows.length ? ageRows : [['Sin datos', '0']]} />
      <Text style={styles.h2}>Distribución por departamento</Text>
      <TwoColTable headers={['Departamento', 'Participantes (n)']} rows={deptRows.length ? deptRows : [['Sin datos', '0']]} />
    </PageFrame>
  );
}

function GuideOnePage({ g1 }: { g1: NonNullable<Nom35ExecutiveGlobalResult['g1']> }) {
  const toRows = (table: Record<string, { requiereAtencion: number; noRequiere: number; total: number }>) =>
    Object.entries(table).map(([k, v]) => [k, String(v.requiereAtencion), `${v.noRequiere}`] as [string, string, string]);
  return (
    <PageFrame>
      <Text style={styles.h1}>I. Acontecimientos Traumáticos Severos (Guía I)</Text>
      <Text style={styles.p}>Se identifica a los trabajadores que requieren valoración clínica tras haber presenciado o sufrido eventos traumáticos durante o con motivo del trabajo.</Text>
      <Text style={styles.p}>{summarizeG1Interpretation(g1)}</Text>

      <Text style={styles.h2}>Distribución General</Text>
      <ThreeColTable headers={['Concepto', 'Valor', 'Riesgo']} rows={[['Total evaluados', String(g1.totalEvaluated), '—'], ['Requiere atención clínica', String(g1.requiresAttentionCount), 'Prioritario']]} />

      <Text style={styles.h2}>Distribución por Sexo</Text>
      <ThreeColTable headers={['Sexo', 'Requiere Atención', 'No Requiere']} rows={toRows(g1.crosses.bySex)} />
      <View style={styles.interpretationBox}><Text style={styles.interpretationText}>{analyzeG1DemographicExecutive(g1.crosses.bySex, 'sexo')}</Text></View>

      <Text style={styles.h2}>Distribución por Edad</Text>
      <ThreeColTable headers={['Edad', 'Requiere Atención', 'No Requiere']} rows={toRows(g1.crosses.byAgeRange)} />
      <View style={styles.interpretationBox}><Text style={styles.interpretationText}>{analyzeG1DemographicExecutive(g1.crosses.byAgeRange, 'edad')}</Text></View>

      <Text style={styles.h2}>Distribución por Departamento</Text>
      <ThreeColTable headers={['Departamento', 'Requiere Atención', 'No Requiere']} rows={toRows(g1.crosses.byDepartment)} />
      <View style={styles.interpretationBox}><Text style={styles.interpretationText}>{analyzeG1DemographicExecutive(g1.crosses.byDepartment, 'departamento')}</Text></View>
    </PageFrame>
  );
}

function CategoryPages({ g2g3 }: { g2g3: NonNullable<Nom35ExecutiveGlobalResult['g2g3']> }) {
  const mapped = buildOfficialCategoryRows(g2g3);
  return (
    <>
      {mapped.map((section, i) => (
        <PageFrame key={section.category.title}>
          <Text style={styles.h1}>II.{i + 1} {section.category.title}</Text>
          <Text style={styles.p}>{section.category.intro}</Text>
          <CategoryTable
            categoryName={section.category.title}
            domains={section.rows}
            interpretationText={categoryInterpretationText(section.rows)}
            expandedNarrative={expandedCategoryNarrative(section.category, section.rows)}
          />
          <Text style={styles.h2}>Acciones Sugeridas para esta Categoría</Text>
          <Text style={styles.p}>{suggestedActionsTextForCategory(section.rows)}</Text>
        </PageFrame>
      ))}
    </>
  );
}

const CROSS_RISK_SIX_HEADERS: SixHeaders = ['Criterio', 'Nulo', 'Bajo', 'Medio', 'Alto', 'Muy Alto'];

function CrossRiskPage({ g2g3 }: { g2g3: NonNullable<Nom35ExecutiveGlobalResult['g2g3']> }) {
  const makeRows = (table: Record<string, { nulo: number; bajo: number; medio: number; alto: number; muyAlto: number; total: number }>): SixRow[] =>
    Object.entries(table).map(
      ([k, v]) =>
        [k, String(v.nulo), String(v.bajo), String(v.medio), String(v.alto), String(v.muyAlto)] as SixRow
    );
  return (
    <PageFrame>
      <Text style={styles.h1}>III. Cruces Demográficos de Riesgo (G2/G3)</Text>
      <Text style={styles.p}>
        Distribución de niveles de riesgo (Nulo, Bajo, Medio, Alto, Muy Alto) por sexo, edad y departamento. Los porcentajes y conteos reflejan patrones de grupo; no deben interpretarse como diagnósticos individuales ni como etiquetas sobre las personas. Conviene complementar con entrevistas, revisión de indicadores operativos y criterios de equidad antes de focalizar acciones.
      </Text>

      <Text style={styles.h2}>Riesgo por Sexo</Text>
      <SixColTable headers={CROSS_RISK_SIX_HEADERS} rows={makeRows(g2g3.crosses.riskBySex)} />
      <View style={styles.interpretationBox}><Text style={styles.interpretationText}>{analyzeRiskDemographicExecutive(g2g3.crosses.riskBySex, 'sexo')}</Text></View>

      <Text style={styles.h2}>Riesgo por Edad</Text>
      <SixColTable headers={CROSS_RISK_SIX_HEADERS} rows={makeRows(g2g3.crosses.riskByAgeRange)} />
      <View style={styles.interpretationBox}><Text style={styles.interpretationText}>{analyzeRiskDemographicExecutive(g2g3.crosses.riskByAgeRange, 'edad')}</Text></View>

      <Text style={styles.h2}>Riesgo por Departamento</Text>
      <SixColTable headers={CROSS_RISK_SIX_HEADERS} rows={makeRows(g2g3.crosses.riskByDepartment)} />
      <View style={styles.interpretationBox}><Text style={styles.interpretationText}>{analyzeRiskDemographicExecutive(g2g3.crosses.riskByDepartment, 'departamento')}</Text></View>
    </PageFrame>
  );
}

function ConclusionsPage({ data }: { data: Nom35ExecutiveGlobalResult }) {
  const globalRisk = data.g2g3?.globalRiskLevel ?? 'Nulo';
  const plan = nom35KeyFindingsAndPhasedPlan(data);
  const hasG2G3 = Boolean(data.g2g3);
  const conclusion = !hasG2G3
    ? 'Cuando el informe se basa principalmente en la Guía I, la conclusión ejecutiva se orienta a protocolos de atención, confidencialidad y acompañamiento; integre además la Guía II o III en ciclos posteriores para el panorama de factores psicosociales.'
    : globalRisk === 'Alto' || globalRisk === 'Muy Alto'
      ? 'En conjunto, los resultados sugieren priorizar un abordaje estructurado de factores psicosociales y del entorno organizacional, articulando a RH, líderes y SST con plazos claros.'
      : globalRisk === 'Medio'
        ? 'En conjunto, los resultados sugieren un enfoque preventivo sostenido: intervenir de forma focalizada donde el riesgo suba y mantener lo que ya funciona como factor protector.'
        : 'En conjunto, los resultados sugieren un panorama relativamente acotado en términos globales; conviene no relajar la prevención y seguir monitoreando categorías puntuales.';
  return (
    <PageFrame>
      <Text style={styles.h1}>IV. Conclusiones y Recomendaciones</Text>
      <Text style={styles.p}>
        {hasG2G3 ? (
          <>
            El análisis integral sugiere que el centro de trabajo presenta un nivel global de riesgo{' '}
            <Text style={{ fontWeight: 'bold', color: riskColor(globalRisk) }}>{globalRisk}</Text>
            {' '}(según la guía II o III aplicada y el promedio del centro).
          </>
        ) : (
          <>
            Este cierre integra la información disponible en la exportación. Para riesgo global por factores psicosociales (Guía II/III) no hay bloque cuantitativo en este informe; revise la sección de Guía I y el resumen ejecutivo.
          </>
        )}
      </Text>
      <Text style={styles.p}>{conclusion}</Text>
      <Text style={styles.h2}>Hallazgos y urgencia</Text>
      <Text style={styles.p}>{plan.findings}</Text>
      <Text style={styles.p}>{plan.urgency}</Text>
      <Text style={styles.h2}>Plan sugerido por fases</Text>
      <Text style={styles.p}><Text style={{ fontWeight: 'bold' }}>a) 0-30 días (inmediatas): </Text>{plan.phaseImmediate}</Text>
      <Text style={styles.p}><Text style={{ fontWeight: 'bold' }}>b) 1-3 meses (corto plazo): </Text>{plan.phaseShort}</Text>
      <Text style={styles.p}><Text style={{ fontWeight: 'bold' }}>c) 3-6 meses (seguimiento): </Text>{plan.phaseFollowUp}</Text>
      <ThreeColTable
        headers={['Línea de acción', 'Prioridad', 'Temporalidad']}
        rows={
          hasG2G3
            ? [
                ['Fortalecer protocolos y política de prevención NOM-035', globalRisk === 'Alto' || globalRisk === 'Muy Alto' ? 'Alta' : 'Media', '0-3 meses'],
                ['Intervenciones focalizadas en categorías críticas', globalRisk === 'Medio' ? 'Media' : 'Alta', '1-6 meses'],
                ['Seguimiento y reevaluación periódica', 'Alta', 'Semestral'],
              ]
            : [
                ['Fortalecer protocolos de atención y confidencialidad (Guía I)', 'Alta', '0-3 meses'],
                ['Completar evaluación Guía II/III en el siguiente ciclo para panorama integral', 'Media', '1-6 meses'],
                ['Seguimiento documental y revisión con asesoría especializada', 'Alta', 'Semestral'],
              ]
        }
      />
      <Text style={styles.closingText}>{plan.responsibilityNote}</Text>
      <Text style={styles.closingText}>
        El presente informe refleja el estado psicosocial del centro de trabajo al momento de la evaluación. Es responsabilidad del centro de trabajo implementar y dar seguimiento a las acciones aquí descritas.
      </Text>
      <View style={{ flexDirection: 'row', justifyContent: 'space-around', marginTop: 80 }} wrap={false}>
        <View style={{ width: '40%', borderTopWidth: 1, borderTopColor: '#374151', paddingTop: 8, alignItems: 'center' }}>
          <Text style={{ fontSize: 9, color: COLORS.text }}>Analista NOM-035 / Responsable de RH</Text>
        </View>
        <View style={{ width: '40%', borderTopWidth: 1, borderTopColor: '#374151', paddingTop: 8, alignItems: 'center' }}>
          <Text style={{ fontSize: 9, color: COLORS.text }}>Representante del Centro de Trabajo</Text>
        </View>
      </View>
    </PageFrame>
  );
}

export function Nom35ExecutiveReportPdf(props: Nom35ExecutiveReportPdfProps) {
  const { data } = props;
  const g1Evaluated = data.g1?.totalEvaluated ?? 0;
  const g2g3Evaluated = data.g2g3?.validAssessmentCount ?? 0;
  // Evita doble conteo cuando la misma persona contestó Guía I y Guía II/III.
  const evaluated = Math.max(g1Evaluated, g2g3Evaluated);
  const totalPopulation = evaluated;
  const showDemographicProfile = Boolean(data.g2g3 || data.g1);
  return (
    <Document>
      <CoverPage {...props} />
      <IntroMethodologyPage totalPopulation={totalPopulation} evaluated={evaluated} />
      <ExecutiveSummaryPage data={data} />
      {showDemographicProfile ? <DemographicProfilePage data={data} /> : null}
      {data.g1 ? <GuideOnePage g1={data.g1} /> : null}
      {data.g2g3 ? <CategoryPages g2g3={data.g2g3} /> : null}
      {data.g2g3 ? <CrossRiskPage g2g3={data.g2g3} /> : null}
      <ConclusionsPage data={data} />
      <HeaderFixed />
      <FooterFixed />
    </Document>
  );
}

export type ExecutivePdfDownloadButtonProps = Nom35ExecutiveReportPdfProps & { className?: string };

export function ExecutivePdfDownloadButton({ className, ...props }: ExecutivePdfDownloadButtonProps) {
  const safeFileName = `Informe-Ejecutivo-NOM035-${props.companyName.replace(/[^\w\s-]/g, '').trim().replace(/\s+/g, '-') || 'centro'}.pdf`;
  return (
    <PDFDownloadLink document={<Nom35ExecutiveReportPdf {...props} />} fileName={safeFileName} className={cn(className)}>
      {({ loading }) => (
        <span className="inline-flex items-center gap-2">
          <FileDown className="h-4 w-4 shrink-0" />
          {loading ? 'Generando informe...' : 'Descargar Informe Ejecutivo'}
        </span>
      )}
    </PDFDownloadLink>
  );
}
